import React, { useState, useEffect, useMemo } from 'react';
import {
    FaClipboardCheck,
    FaSync,
    FaCheck,
    FaTimes,
    FaSpinner,
    FaExclamationTriangle,
    FaCheckCircle,
    FaChevronDown,
    FaChevronUp,
    FaInfoCircle,
    FaSave,
} from 'react-icons/fa';
import {
    reviewCompletion,
    saveReviewDecisions,
} from '../../services/onboardingService';

const getActionLabel = (value) => {
    if (!value) return '';
    if (value === 'keep_existing') return 'Keep existing value';
    if (value === 'not_applicable') return 'Not applicable';
    if (value.startsWith('use_existing:')) {
        return `Confirm record ${value.substring(13)}`;
    }
    return value;
};

const CompletionReview = ({
    serverID,
    purgatoryID,
    item,
    disabled = false,
}) => {
    const [busy, setBusy] = useState(false);
    const [report, setReport] = useState(null);
    const [actions, setActions] = useState({}); // key -> action string
    const [reasons, setReasons] = useState({}); // key -> reason string
    const [error, setError] = useState(null);
    const [message, setMessage] = useState(null);
    const [showCompleteModal, setShowCompleteModal] = useState(false);
    const [openSections, setOpenSections] = useState({ profile: true, training: true });

    // Reset when item or candidate updates
    useEffect(() => {
        setReport(null);
        setActions({});
        setReasons({});
        setError(null);
        setMessage(null);
    }, [serverID, purgatoryID, item?.updatedAt, item?.reviewStatus]);

    const handleCheckRemaining = async () => {
        if (busy || disabled || !serverID || !purgatoryID) return;
        setBusy(true);
        setError(null);
        setMessage(null);

        try {
            const res = await reviewCompletion(serverID, purgatoryID);
            setReport(res);
            setActions({});
            setReasons({});
            setMessage('Latest source and employee records checked.');
        } catch (err) {
            console.error('Failed to review completion:', err);
            setError(err?.message || 'Failed to check outstanding review items.');
        } finally {
            setBusy(false);
        }
    };

    const handleActionChange = (key, actionVal) => {
        setActions((prev) => {
            const next = { ...prev };
            if (!actionVal) {
                delete next[key];
            } else {
                next[key] = actionVal;
            }
            return next;
        });
    };

    const handleReasonChange = (key, reasonVal) => {
        setReasons((prev) => ({ ...prev, [key]: reasonVal }));
    };

    const toggleSection = (sec) => {
        setOpenSections((prev) => ({ ...prev, [sec]: !prev[sec] }));
    };

    const items = useMemo(() => {
        return report?.items || [];
    }, [report]);

    const pendingItems = useMemo(() => {
        return items.filter((i) => i.resolved !== true);
    }, [items]);

    const canComplete = useMemo(() => {
        if (!report) return false;
        if (report.status === 'approved') return false;
        // If there are pending items, every one must have a decision and non-empty reason
        return pendingItems.every((i) => {
            const act = actions[i.key];
            const rsn = (reasons[i.key] || '').trim();
            return Boolean(act && rsn.length > 0);
        });
    }, [report, pendingItems, actions, reasons]);

    const hasAnyDecisionsToSave = useMemo(() => {
        return Object.keys(actions).length > 0;
    }, [actions]);

    const handleSave = async (complete = false) => {
        if (busy || !report) return;

        // Verify that every chosen action has a reason
        const invalidEntry = Object.entries(actions).some(([key, act]) => {
            const rsn = (reasons[key] || '').trim();
            return !rsn;
        });

        if (invalidEntry) {
            setError('Please provide a reason / evidence rationale for every selected decision.');
            return;
        }

        setBusy(true);
        setError(null);
        setMessage(null);

        const decisionList = Object.entries(actions).map(([key, action]) => ({
            key,
            action,
            reason: (reasons[key] || '').trim(),
        }));

        try {
            const res = await saveReviewDecisions({
                serverID,
                purgatoryID,
                assessmentFingerprint: report.fingerprint,
                confirmComplete: complete,
                decisions: decisionList,
            });

            setReport(res);
            setActions({});
            setReasons({});
            setShowCompleteModal(false);

            if (res?.status === 'approved') {
                setMessage('Onboarding review complete! All sections finalized.');
            } else if (complete) {
                setMessage('Review finalized successfully.');
            } else {
                setMessage('Decisions saved. Outstanding items remain visible.');
            }
        } catch (err) {
            console.error('Failed to save decisions:', err);
            setError(err?.message || 'Failed to save review decisions.');
        } finally {
            setBusy(false);
        }
    };

    const sections = report?.sections || {};

    return (
        <div style={{
            backgroundColor: '#ffffff',
            borderRadius: '10px',
            border: '1px solid #e2e8f0',
            padding: '20px',
            marginBottom: '20px',
            boxShadow: '0 1px 3px rgba(0,0,0,0.05)',
        }}>
            {/* Header */}
            <div style={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                flexWrap: 'wrap',
                gap: '12px',
                marginBottom: '12px',
                paddingBottom: '12px',
                borderBottom: '1px solid #edf2f7',
            }}>
                <div>
                    <h3 style={{
                        margin: 0,
                        fontSize: '16px',
                        fontWeight: '800',
                        color: '#183D44',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '8px',
                    }}>
                        <FaClipboardCheck style={{ color: '#2b6cb0' }} />
                        Completion Review & Sign-Off
                    </h3>
                    <p style={{ margin: '4px 0 0 0', fontSize: '13px', color: '#718096' }}>
                        Audit outstanding items, record decision rationales, and complete onboarding sign-off.
                    </p>
                </div>

                <button
                    type="button"
                    onClick={handleCheckRemaining}
                    disabled={busy || disabled}
                    style={{
                        display: 'inline-flex',
                        alignItems: 'center',
                        gap: '6px',
                        backgroundColor: '#ffffff',
                        color: '#2b6cb0',
                        border: '1px solid #bee3f8',
                        padding: '8px 16px',
                        borderRadius: '6px',
                        fontSize: '13px',
                        fontWeight: '600',
                        cursor: busy || disabled ? 'not-allowed' : 'pointer',
                        boxShadow: '0 1px 2px rgba(0,0,0,0.05)',
                    }}
                >
                    {busy ? (
                        <>
                            <FaSpinner className="fa-spin" />
                            <span>Working…</span>
                        </>
                    ) : (
                        <>
                            <FaSync />
                            <span>{report ? 'Re-check Remaining Items' : 'Check Remaining Items'}</span>
                        </>
                    )}
                </button>
            </div>

            {/* Explanatory Banner */}
            <div style={{
                backgroundColor: '#f7fafc',
                border: '1px solid #e2e8f0',
                borderRadius: '6px',
                padding: '10px 14px',
                marginBottom: '14px',
                fontSize: '12.5px',
                color: '#4a5568',
                lineHeight: '1.45',
            }}>
                Approve changes in the profile and training panels above, then check what remains. Keeping a value or marking a course not applicable requires an audit reason. Final sign-off completes the data review; it does not validate DVLA checks or change account access.
            </div>

            {/* Error Message */}
            {error && (
                <div style={{
                    backgroundColor: '#fff5f5',
                    border: '1px solid #fed7d7',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#c53030',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'flex-start',
                    gap: '8px',
                }}>
                    <FaExclamationTriangle style={{ marginTop: '2px', flexShrink: 0 }} />
                    <span>{error}</span>
                </div>
            )}

            {/* Success Message */}
            {message && (
                <div style={{
                    backgroundColor: '#f0fff4',
                    border: '1px solid #c6f6d5',
                    borderRadius: '6px',
                    padding: '10px 14px',
                    marginBottom: '14px',
                    color: '#276749',
                    fontSize: '13px',
                    display: 'flex',
                    alignItems: 'center',
                    gap: '8px',
                }}>
                    <FaCheckCircle style={{ flexShrink: 0 }} />
                    <span>{message}</span>
                </div>
            )}

            {/* Report Content */}
            {report ? (
                <div>
                    {report.skippedEmptyFields !== undefined && (
                        <div style={{ fontSize: '12.5px', color: '#718096', marginBottom: '12px' }}>
                            • <strong>{report.skippedEmptyFields}</strong> empty optional source fields were left unchanged.
                        </div>
                    )}

                    {/* Collapsible Sections: Profile and Training */}
                    {['profile', 'training'].map((sectionKey) => {
                        const secData = sections[sectionKey] || {};
                        const secItems = pendingItems.filter((i) => i.section === sectionKey);
                        const isOpen = openSections[sectionKey] ?? true;

                        return (
                            <div key={sectionKey} style={{
                                border: '1px solid #e2e8f0',
                                borderRadius: '8px',
                                marginBottom: '12px',
                                overflow: 'hidden',
                            }}>
                                <button
                                    type="button"
                                    onClick={() => toggleSection(sectionKey)}
                                    style={{
                                        width: '100%',
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center',
                                        padding: '12px 16px',
                                        backgroundColor: '#f7fafc',
                                        border: 'none',
                                        borderBottom: isOpen ? '1px solid #e2e8f0' : 'none',
                                        cursor: 'pointer',
                                    }}
                                >
                                    <span style={{ fontSize: '13.5px', fontWeight: '700', color: '#2d3748', textTransform: 'capitalize' }}>
                                        {sectionKey} Section — {secData.remaining ?? secItems.length} outstanding
                                    </span>
                                    {isOpen ? <FaChevronUp style={{ color: '#718096' }} /> : <FaChevronDown style={{ color: '#718096' }} />}
                                </button>

                                {isOpen && (
                                    <div style={{ padding: '12px 16px' }}>
                                        {secItems.length === 0 ? (
                                            <div style={{
                                                padding: '10px 0',
                                                fontSize: '13px',
                                                color: '#2f855a',
                                                display: 'flex',
                                                alignItems: 'center',
                                                gap: '8px',
                                            }}>
                                                <FaCheckCircle />
                                                <span>No outstanding items in this section.</span>
                                            </div>
                                        ) : (
                                            secItems.map((itemRow) => {
                                                const key = itemRow.key;
                                                const opts = itemRow.options || [];
                                                const chosenAction = actions[key] || '';
                                                const reasonVal = reasons[key] || '';

                                                return (
                                                    <div key={key} style={{
                                                        border: '1px solid #edf2f7',
                                                        backgroundColor: '#ffffff',
                                                        borderRadius: '8px',
                                                        padding: '12px',
                                                        marginBottom: '10px',
                                                    }}>
                                                        <div style={{ fontSize: '13.5px', fontWeight: '700', color: '#183D44' }}>
                                                            {itemRow.label}
                                                        </div>
                                                        {itemRow.detail && (
                                                            <div style={{ fontSize: '12.5px', color: '#718096', marginTop: '2px' }}>
                                                                {itemRow.detail}
                                                            </div>
                                                        )}

                                                        {opts.length === 0 ? (
                                                            <div style={{
                                                                marginTop: '8px',
                                                                fontSize: '12px',
                                                                color: '#c53030',
                                                                fontWeight: '600',
                                                            }}>
                                                                Resolve this through the profile review above before completing.
                                                            </div>
                                                        ) : (
                                                            <div style={{ marginTop: '10px' }}>
                                                                {/* Decision dropdown */}
                                                                <div style={{ marginBottom: '8px' }}>
                                                                    <label style={{ fontSize: '12px', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
                                                                        Decision:
                                                                    </label>
                                                                    <select
                                                                        value={chosenAction}
                                                                        onChange={(e) => handleActionChange(key, e.target.value)}
                                                                        disabled={busy}
                                                                        style={{
                                                                            width: '100%',
                                                                            padding: '7px 10px',
                                                                            borderRadius: '6px',
                                                                            border: '1px solid #cbd5e0',
                                                                            fontSize: '13px',
                                                                            backgroundColor: '#ffffff',
                                                                        }}
                                                                    >
                                                                        <option value="">-- Select Decision --</option>
                                                                        {opts.map((opt, idx) => (
                                                                            <option key={idx} value={opt}>
                                                                                {getActionLabel(opt)}
                                                                            </option>
                                                                        ))}
                                                                    </select>
                                                                </div>

                                                                {/* Mandatory Reason Input */}
                                                                {chosenAction && (
                                                                    <div>
                                                                        <label style={{ fontSize: '12px', fontWeight: '600', color: '#4a5568', display: 'block', marginBottom: '4px' }}>
                                                                            Reason / Evidence Checked (Required):
                                                                        </label>
                                                                        <textarea
                                                                            value={reasonVal}
                                                                            onChange={(e) => handleReasonChange(key, e.target.value)}
                                                                            disabled={busy}
                                                                            placeholder="State the rationale or audit evidence verified..."
                                                                            rows={2}
                                                                            maxLength={1000}
                                                                            style={{
                                                                                width: '100%',
                                                                                padding: '8px 10px',
                                                                                borderRadius: '6px',
                                                                                border: '1px solid #cbd5e0',
                                                                                fontSize: '12.5px',
                                                                                resize: 'vertical',
                                                                                boxSizing: 'border-box',
                                                                            }}
                                                                        />
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                );
                                            })
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    })}

                    {/* Action Buttons */}
                    <div style={{
                        marginTop: '16px',
                        paddingTop: '14px',
                        borderTop: '1px solid #edf2f7',
                        display: 'flex',
                        justifyContent: 'flex-end',
                        gap: '10px',
                    }}>
                        <button
                            type="button"
                            onClick={() => handleSave(false)}
                            disabled={busy || disabled || !hasAnyDecisionsToSave}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#ffffff',
                                color: '#4a5568',
                                border: '1px solid #cbd5e0',
                                padding: '9px 18px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '600',
                                cursor: busy || disabled || !hasAnyDecisionsToSave ? 'not-allowed' : 'pointer',
                            }}
                        >
                            <FaSave />
                            <span>Save Decisions</span>
                        </button>

                        <button
                            type="button"
                            onClick={() => setShowCompleteModal(true)}
                            disabled={busy || disabled || !canComplete}
                            style={{
                                display: 'inline-flex',
                                alignItems: 'center',
                                gap: '6px',
                                backgroundColor: '#183D44',
                                color: '#ffffff',
                                border: 'none',
                                padding: '9px 20px',
                                borderRadius: '6px',
                                fontSize: '13px',
                                fontWeight: '700',
                                cursor: busy || disabled || !canComplete ? 'not-allowed' : 'pointer',
                                boxShadow: '0 1px 3px rgba(0,0,0,0.1)',
                            }}
                        >
                            <FaCheck />
                            <span>Complete Review</span>
                        </button>
                    </div>
                </div>
            ) : (
                <div style={{ fontSize: '12.5px', color: '#718096', fontStyle: 'italic' }}>
                    Click "Check Remaining Items" to assess overall completion state across all profile and training sections.
                </div>
            )}

            {/* Final Sign-off Modal */}
            {showCompleteModal && (
                <div style={{
                    position: 'fixed',
                    top: 0,
                    left: 0,
                    right: 0,
                    bottom: 0,
                    backgroundColor: 'rgba(0, 0, 0, 0.5)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    zIndex: 1050,
                    padding: '20px',
                }}>
                    <div style={{
                        backgroundColor: '#ffffff',
                        borderRadius: '10px',
                        maxWidth: '520px',
                        width: '100%',
                        padding: '24px',
                        boxShadow: '0 10px 25px rgba(0, 0, 0, 0.2)',
                    }}>
                        <div style={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            marginBottom: '14px',
                        }}>
                            <h3 style={{
                                margin: 0,
                                fontSize: '16px',
                                fontWeight: '700',
                                color: '#183D44',
                                display: 'flex',
                                alignItems: 'center',
                                gap: '8px',
                            }}>
                                <FaClipboardCheck /> Complete onboarding review?
                            </h3>
                            <button
                                type="button"
                                onClick={() => setShowCompleteModal(false)}
                                disabled={busy}
                                style={{
                                    background: 'none',
                                    border: 'none',
                                    fontSize: '16px',
                                    color: '#a0aec0',
                                    cursor: 'pointer',
                                }}
                            >
                                <FaTimes />
                            </button>
                        </div>

                        <div style={{
                            fontSize: '13px',
                            color: '#4a5568',
                            lineHeight: '1.5',
                            marginBottom: '18px',
                        }}>
                            <p style={{ margin: '0 0 10px 0' }}>
                                Confirm that you have reviewed all applicable profile fields and training records for this candidate.
                            </p>
                            <p style={{ margin: 0, color: '#718096', fontSize: '12px' }}>
                                This completes the data review; it does not validate DVLA checks or change account access.
                            </p>
                        </div>

                        <div style={{
                            display: 'flex',
                            justifyContent: 'flex-end',
                            gap: '10px',
                        }}>
                            <button
                                type="button"
                                onClick={() => setShowCompleteModal(false)}
                                disabled={busy}
                                style={{
                                    backgroundColor: '#edf2f7',
                                    color: '#4a5568',
                                    border: '1px solid #cbd5e0',
                                    padding: '8px 16px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '600',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                Cancel
                            </button>
                            <button
                                type="button"
                                onClick={() => handleSave(true)}
                                disabled={busy}
                                style={{
                                    display: 'inline-flex',
                                    alignItems: 'center',
                                    gap: '6px',
                                    backgroundColor: '#183D44',
                                    color: '#ffffff',
                                    border: 'none',
                                    padding: '8px 18px',
                                    borderRadius: '6px',
                                    fontSize: '13px',
                                    fontWeight: '700',
                                    cursor: busy ? 'not-allowed' : 'pointer',
                                }}
                            >
                                {busy ? (
                                    <>
                                        <FaSpinner className="fa-spin" />
                                        <span>Completing…</span>
                                    </>
                                ) : (
                                    <span>Complete Review</span>
                                )}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CompletionReview;
